import { MutationTree } from 'vuex'

import { AppliedPoint, OrderConfirmState, SelectedCoupon } from './types'

import { log } from '@/log'
import { DeliveryMethodDetail, OkihaiPlace } from '@/models/delivery'
import { ResolverResource } from '@/services/payment/variable-resolver'
import { DeliveryConfig, Payment } from '@spa/store/modules/orderConfirm/types'

const namespace = 'orderConfirm'

/*
 * 支払い方法毎に mutation を定義するのではなく、単一のI/Fにして
 * 支払い方法ごとの設定値・初期化処理の構築は独立したモジュールとして
 * client/spa/src/components/pages/order/model/payment/initialization/builder.ts
 * に分離する想定.
 *
 * client/spa/src/store/modules/orderConfirm/actions.ts と同じ理由で残留.
 */
export const mutations: MutationTree<OrderConfirmState> = {
  setPayment(state, payload: { payment: Payment }) {
    state.payment = payload.payment

    log.debug({ commit: `${namespace}/setPayment`, state })
  },

  unsetPayment(state) {
    state.payment = null

    log.debug({ commit: `${namespace}/unsetPayment`, state })
  },

  putPaymentVariableResource(state, payload: { partialResource: Partial<ResolverResource> }) {
    if (!state.payment) {
      log.warn('state.payment is not set')

      return
    }
    state.payment.variableResource = {
      ...state.payment.variableResource,
      ...payload.partialResource,
    }

    log.debug({ commit: `${namespace}/putPaymentVariableResource`, payload, state })
  },

  initializeDelivery(state, payload: { id: string, presetMethod: string }) {
    // TODO:
    // Order.vue の initDeliveryConfigSpec をベースに処理を統合
    // 配送情報の設定をひとかたまりの手順にして、中間状態をなくす
    const { id, presetMethod } = payload

    state.delivery = {
      deliveryType: 'registered',
      id,
      // 以下はsetDeliveryMethodで改めて設定される
      deliveryMethod: presetMethod,
      unattendedDelivery: null,
    }

    log.debug({ commit: `${namespace}/setDelivery`, state })
  },

  setDeliveryMethod(state, payload: { method: DeliveryMethodDetail }) {
    if (state.delivery) {
      const { method } = payload
      state.delivery = {
        deliveryType: state.delivery.deliveryType,
        id: state.delivery.id,
        deliveryMethod: method.externalId,
        unattendedDelivery: method.okihaiPlaces && method.okihaiPlaces[0]
          ? method.okihaiPlaces[0].id
          : null,
      }
    } else {
      log.warn(`${namespace}/setDeliveryMethod was ignored because delivery is null`, state)
    }

    log.debug({ commit: `${namespace}/setDeliveryMethod`, state })
  },

  unsetDelivery(state) {
    state.delivery = null

    log.debug({ commit: `${namespace}/unsetDelivery`, state })
  },

  setDeliveryDate(state, payload: { deliveryPlanDate: string, deliveryTimeSlot: string }) {
    state.deliveryDate = payload

    log.debug({ commit: `${namespace}/setDeliveryDate`, state })
  },

  applyPoint(state, applied: AppliedPoint) {
    state.point = applied

    log.debug({ commit: `${namespace}/applyPoint`, state })
  },

  selectCoupon(state, selected: SelectedCoupon) {
    state.coupon = selected

    log.debug({ commit: `${namespace}/selectCoupon`, state })
  },

  setComplete(state, payload) {
    state.complete = payload

    log.debug({ commit: `${namespace}/setComplete`, state })
  },

  setSelectedDeliveryConfig(state, payload: DeliveryConfig) {
    state.selectedDeliveryConfig = payload

    log.debug({ commit: `${namespace}/setSelectedDeliveryConfig`, state })
  },

  setOkihaiPlace(state, payload: OkihaiPlace) {
    if (state.delivery) {
      state.delivery.unattendedDelivery = payload.id
    } else {
      log.warn(`${namespace}/setOkihaiPlace was ignored because delivery is null`, state)
    }

    log.debug({ commit: `${namespace}/setOkihaiPlace`, state })
  },
}
